import type { Annotation } from "@/types/annotation";
import type { Prediction } from "@/utils/api/inference";

type Box = { x: number; y: number; width: number; height: number };

/**
 * Calculate Intersection over Union (IoU) between two boxes.
 * Boxes are expected to have x, y at center with width and height.
 */
export function calculateIoU(boxA: Box, boxB: Box): number {
    // Convert center-based coordinates to corner-based
    const boxALeft = boxA.x - boxA.width / 2;
    const boxARight = boxA.x + boxA.width / 2;
    const boxATop = boxA.y - boxA.height / 2;
    const boxABottom = boxA.y + boxA.height / 2;

    const boxBLeft = boxB.x - boxB.width / 2;
    const boxBRight = boxB.x + boxB.width / 2;
    const boxBTop = boxB.y - boxB.height / 2;
    const boxBBottom = boxB.y + boxB.height / 2;

    // Calculate intersection
    const intersectLeft = Math.max(boxALeft, boxBLeft);
    const intersectRight = Math.min(boxARight, boxBRight);
    const intersectTop = Math.max(boxATop, boxBTop);
    const intersectBottom = Math.min(boxABottom, boxBBottom);

    const intersectWidth = Math.max(0, intersectRight - intersectLeft);
    const intersectHeight = Math.max(0, intersectBottom - intersectTop);
    const intersectArea = intersectWidth * intersectHeight;

    // Calculate union
    const boxAArea = boxA.width * boxA.height;
    const boxBArea = boxB.width * boxB.height;
    const unionArea = boxAArea + boxBArea - intersectArea;

    if (unionArea === 0) return 0;

    return intersectArea / unionArea;
}

/**
 * Calculate Intersection over Minimum area (IoMin) between two boxes.
 * This is useful when you want to detect if a smaller box is mostly contained within a larger one.
 */
export function calculateIoMin(boxA: Box, boxB: Box): number {
    // Convert center-based coordinates to corner-based
    const boxALeft = boxA.x - boxA.width / 2;
    const boxARight = boxA.x + boxA.width / 2;
    const boxATop = boxA.y - boxA.height / 2;
    const boxABottom = boxA.y + boxA.height / 2;

    const boxBLeft = boxB.x - boxB.width / 2;
    const boxBRight = boxB.x + boxB.width / 2;
    const boxBTop = boxB.y - boxB.height / 2;
    const boxBBottom = boxB.y + boxB.height / 2;

    // Calculate intersection
    const intersectLeft = Math.max(boxALeft, boxBLeft);
    const intersectRight = Math.min(boxARight, boxBRight);
    const intersectTop = Math.max(boxATop, boxBTop);
    const intersectBottom = Math.min(boxABottom, boxBBottom);

    const intersectWidth = Math.max(0, intersectRight - intersectLeft);
    const intersectHeight = Math.max(0, intersectBottom - intersectTop);
    const intersectArea = intersectWidth * intersectHeight;

    // Calculate minimum area
    const boxAArea = boxA.width * boxA.height;
    const boxBArea = boxB.width * boxB.height;
    const minArea = Math.min(boxAArea, boxBArea);

    if (minArea === 0) return 0;

    return intersectArea / minArea;
}

/**
 * Filter predictions that overlap with existing annotations using NMS.
 * Returns predictions that don't significantly overlap with existing annotations.
 *
 * @param predictions - New predictions to filter
 * @param existingAnnotations - Existing annotations to compare against
 * @param iouThreshold - IoU threshold above which a prediction is considered a duplicate (default 0.5)
 * @param labelFilter - Optional label to filter predictions by (only dedupe against annotations with same label)
 */
export function deduplicatePredictionsAgainstAnnotations<T extends Box & { class?: string }>(
    predictions: T[],
    existingAnnotations: (Annotation & Box)[],
    iouThreshold: number = 0.5,
    labelFilter?: string
): T[] {
    // Filter annotations by label if specified
    const relevantAnnotations = labelFilter
        ? existingAnnotations.filter((anno) => anno.label === labelFilter)
        : existingAnnotations;

    if (relevantAnnotations.length === 0) {
        return predictions;
    }

    return predictions.filter((prediction) => {
        // Check if this prediction overlaps significantly with any existing annotation
        for (const annotation of relevantAnnotations) {
            // If labelFilter is specified, only compare against same-label annotations
            // If no labelFilter, compare prediction class against annotation label
            if (!labelFilter && prediction.class && prediction.class !== annotation.label) {
                continue;
            }

            const iou = calculateIoU(prediction, annotation);
            if (iou >= iouThreshold) {
                // This prediction overlaps with an existing annotation, filter it out
                return false;
            }
        }
        // No significant overlap found, keep this prediction
        return true;
    });
}

/**
 * Standard NMS within a set of predictions.
 * Sorts by confidence and removes overlapping lower-confidence predictions.
 *
 * @param predictions - Predictions to apply NMS to
 * @param iouThreshold - IoU threshold above which predictions are considered duplicates
 */
export function applyNMS<T extends Box & { confidence: number }>(
    predictions: T[],
    iouThreshold: number = 0.5
): T[] {
    if (predictions.length === 0) return [];

    // Sort by confidence (descending)
    const sorted = [...predictions].sort((a, b) => b.confidence - a.confidence);
    const kept: T[] = [];

    for (const prediction of sorted) {
        let shouldKeep = true;

        for (const keptPrediction of kept) {
            const iou = calculateIoU(prediction, keptPrediction);
            if (iou >= iouThreshold) {
                shouldKeep = false;
                break;
            }
        }

        if (shouldKeep) {
            kept.push(prediction);
        }
    }

    return kept;
}
